import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  useSensor,
  PointerSensor,
  useSensors,
  rectIntersection,
  closestCenter
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  List,
  Typography,
  IconButton,
  Tooltip,
  styled,
  Card,
  Box
} from '@mui/material';
import { GripVertical, Info, Maximize2, Minimize2 } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import DraggableItem from './DraggableItem';
import Droppable from './DroppableItem';
import SortableRow from './SortableRow';
import DependencyGraph from '../../../configuration/AnalysisConfiguration/WorkflowMonitor/dependancyGraph';
import { setWorkflowConfig } from 'store/annotations/annotationSlice';

interface Label {
  id: string;
  label_name: string;
}

interface WorkflowLabel {
  id: string;
  label_id: string;
  label: string;
  dependency: string[] | null;
  mandatory: boolean;
  start_time: string;
  end_time: string;
}

interface LabelWorkflowProps {
  labels: Label[];
  selectedWorkflow: WorkflowLabel[];
  onWorkflowChange: (workflow: WorkflowLabel[]) => void;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '70vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#102533',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  height: 'calc(60vh - 64px)', // Subtract header height
  paddingBottom: '10px'
}));

const StyledListItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'grab',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&:active': {
    cursor: 'grabbing'
  }
}));

const DropZoneContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOver' && prop !== 'isEmpty'
})<{ isOver?: boolean; isEmpty?: boolean }>(({ theme, isOver, isEmpty }) => ({
  position: 'relative',
  minHeight: isEmpty ? '300px' : 'auto',
  border: `2px dashed ${
    isOver ? theme.palette.primary.main : theme.palette.divider
  }`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isOver ? 'whitesmoke' : 'transparent',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  margin: theme.spacing(2),
  padding: theme.spacing(2),

  '&::before': isEmpty && {
    content: '"Drag and drop labels here"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    whiteSpace: 'nowrap',
    pointerEvents: 'none'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  padding: theme.spacing(1.5)
}));

const LabelWorkflow: React.FC<LabelWorkflowProps> = ({
  labels,
  selectedWorkflow,
  onWorkflowChange
}) => {
  const [internalWorkflow, setInternalWorkflow] =
    useState<WorkflowLabel[]>(selectedWorkflow);

  // Sync local state with props when they change
  useEffect(() => {
    setInternalWorkflow(selectedWorkflow);
  }, [selectedWorkflow]);

  const [availableLabels, setAvailableLabels] = useState<Label[]>(labels);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isOver, setIsOver] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const previousLength = useRef(selectedWorkflow.length);

  useEffect(() => {
    if (scrollRef.current && internalWorkflow.length > previousLength.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    previousLength.current = internalWorkflow.length;
  }, [internalWorkflow]);

  useEffect(() => {
    const usedLabelIds = new Set(internalWorkflow.map(item => item.label_id));
    setAvailableLabels(labels.filter(label => !usedLabelIds.has(label.id)));

    // Only call setWorkflowConfig when the workflow actually changes
    setWorkflowConfig(internalWorkflow);
  }, [labels, internalWorkflow]);

  useEffect(() => {
    // Don't notify on initial mount
    if (JSON.stringify(internalWorkflow) !== JSON.stringify(selectedWorkflow)) {
      onWorkflowChange(internalWorkflow);
    }
  }, [internalWorkflow]);

  useEffect(() => {
    const usedLabelIds = new Set(selectedWorkflow.map(item => item.label_id));
    setAvailableLabels(labels.filter(label => !usedLabelIds.has(label.id)));
    setWorkflowConfig(selectedWorkflow);
  }, [labels, selectedWorkflow]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setIsOver(over?.id === 'workflow-dropzone');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event;

    if (over?.id === 'workflow-dropzone' && activeId) {
      const labelToAdd = labels.find(l => l.id === activeId);
      if (!labelToAdd) return;

      const newLabel: WorkflowLabel = {
        id: uuid(),
        label_id: activeId,
        label: labelToAdd.label_name,
        dependency: selectedWorkflow.length === 0 ? [] : [],
        mandatory: false,
        start_time: selectedWorkflow.length === 0 ? '00:00:00' : '',
        end_time: ''
      };

      setInternalWorkflow(prev => [...prev, newLabel]);
      // onWorkflowChange([...selectedWorkflow, newLabel]);
      setAvailableLabels(prev => prev.filter(l => l.id !== activeId));
    }
    setActiveId(null);
    setIsOver(false);
  };

  const handleSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedWorkflow.findIndex(
        item => item.id === active.id
      );
      const newIndex = selectedWorkflow.findIndex(item => item.id === over.id);

      // const newWorkflow = arrayMove(selectedWorkflow, oldIndex, newIndex);
      setInternalWorkflow(prev => arrayMove(prev, oldIndex, newIndex));
      // onWorkflowChange(newWorkflow);
    }
  };

  const handleDelete = useCallback(
    (id: string) => {
      const deletedItem = internalWorkflow.find(item => item.id === id);
      if (deletedItem) {
        const label = labels.find(l => l.id === deletedItem.label_id);
        if (label) {
          setAvailableLabels(prev => [...prev, label]);
        }

        let updatedWorkflow = internalWorkflow
          .filter(item => item.id !== id)
          .map(item => {
            if (item.dependency && item.dependency.includes(id)) {
              const newDependency = item.dependency.filter(
                depId => depId !== id
              );
              return { ...item, dependency: newDependency };
            }
            return item;
          });

        if (updatedWorkflow.length > 0 && internalWorkflow[0].id === id) {
          updatedWorkflow = [
            { ...updatedWorkflow[0], dependency: [] },
            ...updatedWorkflow.slice(1)
          ];
        }

        setInternalWorkflow(updatedWorkflow);
      } else {
        setInternalWorkflow(prev => prev.filter(item => item.id !== id));
      }
    },
    [internalWorkflow, labels]
  );

  const handleDependencyChange = useCallback(
    (id: string, dependencies: string[]) => {
      // Update internal state
      setInternalWorkflow(prev =>
        prev.map(item =>
          item.id === id ? { ...item, dependency: dependencies } : item
        )
      );
    },
    []
  );

  const handleMandatoryChange = useCallback((id: string, value: boolean) => {
    // Update internal state
    setInternalWorkflow(prev =>
      prev.map(item => (item.id === id ? { ...item, mandatory: value } : item))
    );
  }, []);

  const handleTimeChange = useCallback(
    (id: string, type: 'start' | 'end', value: string) => {
      // Update internal state
      setInternalWorkflow(prev =>
        prev.map(item =>
          item.id === id
            ? {
                ...item,
                [type === 'start' ? 'start_time' : 'end_time']: value
              }
            : item
        )
      );
    },
    []
  );

  const validateTimeFormat = (timeString: string): boolean => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    return timeRegex.test(timeString);
  };

  // Graph data
  const nodes = useMemo(() => {
    return selectedWorkflow.map((item, index) => ({
      id: item.id,
      data: {
        label: item.label,
        mandatory: item.mandatory,
        times: `${item.start_time} - ${item.end_time}`
      },
      position: { x: 150 * index, y: 100 }
    }));
  }, [selectedWorkflow]);

  const edges = useMemo(() => {
    return selectedWorkflow
      .filter(item => item.dependency !== null && item.dependency.length > 0)
      .flatMap(item =>
        (item.dependency as string[]).map(dep => ({
          id: `e-${dep}-${item.id}`,
          source: dep,
          target: item.id,
          type: 'smoothstep',
          animated: true
        }))
      );
  }, [selectedWorkflow]);

  const handleGraphToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const workflowRows = useMemo(
    () =>
      internalWorkflow.map((item, index) => (
        <SortableRow
          key={item.id}
          id={item.id}
          index={index}
          workflowItem={item}
          allWorkflowItems={internalWorkflow}
          onDelete={handleDelete}
          onDependencyChange={handleDependencyChange}
          onMandatoryChange={handleMandatoryChange}
          onTimeChange={handleTimeChange}
          isFirst={index === 0}
        />
      )),
    [
      internalWorkflow,
      handleDelete,
      handleDependencyChange,
      handleMandatoryChange,
      handleTimeChange
    ]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
        tolerance: 20
      }
    })
  );

  const customCollisionDetection = (args: any) => {
    // First, use the rectangleIntersection collision detection
    const intersections = rectIntersection(args);

    // If we have intersections, use them
    if (intersections.length > 0) {
      return intersections;
    }

    // Otherwise, fall back to closestCenter
    return closestCenter(args);
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <Grid container spacing={2}>
        {/* Labels List */}
        {!isOpen && (
          <Grid item xs={12} md={2}>
            <StyledCard variant="outlined">
              <HeaderBox className="flex items-center justify-start gap-2">
                <Typography variant="h6" color="common.white">
                  Available Labels
                </Typography>
                <Tooltip title="Drag labels to workflow area">
                  <Info size={20} style={{ marginLeft: '2px' }} color="white" />
                </Tooltip>
              </HeaderBox>
              <ScrollableContent>
                <List sx={{ p: 1 }}>
                  {availableLabels.map(label => (
                    <DraggableItem key={label.id} id={label.id}>
                      <StyledListItem elevation={1}>
                        <GripVertical size={20} />
                        <Typography noWrap>{label.label_name}</Typography>
                      </StyledListItem>
                    </DraggableItem>
                  ))}
                  {availableLabels.length === 0 && (
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ py: 4 }}
                    >
                      No more labels available
                    </Typography>
                  )}
                </List>
              </ScrollableContent>
            </StyledCard>
          </Grid>
        )}

        {/* Workflow Area */}
        {!isOpen && (
          <Grid item xs={12} md={7}>
            <StyledCard variant="outlined">
              <HeaderBox>
                <Typography variant="h6" color="common.white">
                  Define Workflow
                </Typography>
              </HeaderBox>
              <ScrollableContent ref={scrollRef}>
                <Droppable id="workflow-dropzone">
                  <DropZoneContainer
                    isOver={isOver}
                    isEmpty={selectedWorkflow.length === 0}
                  >
                    <TableContainer sx={{ borderRadius: 1 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>Sequence</StyledTableCell>
                            <StyledTableCell>Min Time (mm:ss)</StyledTableCell>
                            <StyledTableCell>Max Time (mm:ss)</StyledTableCell>
                            <StyledTableCell>Dependency</StyledTableCell>
                            <StyledTableCell>Mandatory</StyledTableCell>
                            <StyledTableCell>Actions</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <DndContext
                          onDragEnd={handleSortEnd}
                          modifiers={[restrictToVerticalAxis]}
                          sensors={sensors}
                          // collisionDetection={customCollisionDetection}
                        >
                          <SortableContext
                            items={selectedWorkflow.map(item => item.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <TableBody>{workflowRows}</TableBody>
                            <TableRow
                              style={{ height: '30px', visibility: 'hidden' }}
                            >
                              <TableCell colSpan={6}></TableCell>
                            </TableRow>
                          </SortableContext>
                        </DndContext>
                      </Table>
                    </TableContainer>
                  </DropZoneContainer>
                </Droppable>
              </ScrollableContent>
            </StyledCard>
          </Grid>
        )}

        {/* Dependency Graph */}
        <Grid item xs={12} md={!isOpen ? 3 : 12}>
          <StyledCard variant="outlined">
            <HeaderBox>
              <Typography variant="h6" color="common.white">
                Dependency Graph
              </Typography>
              <IconButton onClick={handleGraphToggle} sx={{ color: 'white' }}>
                {isOpen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </IconButton>
            </HeaderBox>
            <ScrollableContent>
              <Box sx={{ height: '100%' }}>
                <DependencyGraph isOpen={isOpen} nodes={nodes} edges={edges} />
              </Box>
            </ScrollableContent>
          </StyledCard>
        </Grid>
      </Grid>

      <DragOverlay dropAnimation={null}>
        {activeId && (
          <Paper
            elevation={4}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'background.paper',
              cursor: 'grabbing',
              maxWidth: '250px'
            }}
          >
            <GripVertical size={20} />
            <Typography noWrap>
              {labels.find(label => label.id === activeId)?.label_name}
            </Typography>
          </Paper>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default React.memo(LabelWorkflow);
